import React, { useState, useEffect } from 'react';
import { 
  Wine, 
  Edit, 
  Trash2, 
  Search, 
  Plus, 
  Eye, 
  EyeOff,
  Euro,
  Save,
  X,
  Filter
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';
import LoadingSpinner from '../LoadingSpinner';
import { adminAPI } from '../../services/api';
import FileUpload from './FileUpload';
import { getImageUrlWithFallback } from '../../utils/imageUtils';

const AdminBottles = () => {
  const { adminToken } = useAdminAuth();
  const { toast } = useToast();
  
  const [bottles, setBottles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [filters, setFilters] = useState({
    material: '',
    available: '',
    search: ''
  });
  
  const [selectedBottle, setSelectedBottle] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [bottleForm, setBottleForm] = useState({
    id: '',
    name: '',
    description: '',
    price: '',
    capacity: '500',
    material: 'vetro',
    color: '',
    shape: '',
    closureType: 'sughero',
    available: true,
    premium: false,
    image: '',
    manufacturer: '',
    origin: '',
    sustainablePackaging: false,
    recyclable: true
  });

  useEffect(() => {
    loadBottles();
  }, [filters]);

  const loadBottles = async () => {
    try {
      setLoading(true);
      
      // Call real bottles API
      const response = await adminAPI.getBottles(filters, 1, 50);
      if (response.success) {
        setBottles(response.bottles);
      } else {
        throw new Error(response.message || 'Failed to load bottles');
      }
      
    } catch (error) {
      console.error('Error loading bottles:', error);
      toast({
        title: "Errore nel caricamento",
        description: "Impossibile caricare le bottiglie",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBottle = async (bottleId, updateData) => {
    try {
      setUpdating(bottleId);
      
      // Clean and format data for backend
      const cleanedData = {
        name: updateData.name,
        description: updateData.description,
        price: parseFloat(updateData.price),
        capacity: parseInt(updateData.capacity),
        material: updateData.material,
        closureType: updateData.closureType,
        available: updateData.available,
        premium: updateData.premium,
        sustainablePackaging: updateData.sustainablePackaging,
        recyclable: updateData.recyclable
      };
      
      // Add optional fields only if they have values
      if (updateData.color && updateData.color !== '') {
        cleanedData.color = updateData.color;
      }
      if (updateData.shape && updateData.shape !== '') {
        cleanedData.shape = updateData.shape;
      }
      if (updateData.image && updateData.image !== '') {
        cleanedData.image = updateData.image;
      }
      if (updateData.manufacturer && updateData.manufacturer !== '') {
        cleanedData.manufacturer = updateData.manufacturer;
      }
      if (updateData.origin && updateData.origin !== '') {
        cleanedData.origin = updateData.origin;
      }
      
      const response = await adminAPI.updateBottle(bottleId, cleanedData);
      if (response.success) {
        toast({
          title: "Bottiglia aggiornata",
          description: "Le modifiche sono state salvate con successo",
        });
        
        setShowEditModal(false);
        setSelectedBottle(null);
        loadBottles();
      } else {
        throw new Error(response.message || 'Failed to update bottle');
      }
      
    } catch (error) {
      console.error('Error updating bottle:', error);
      toast({
        title: "Errore nell'aggiornamento",
        description: error.message || "Impossibile aggiornare la bottiglia",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  const handleCreateBottle = async (bottleData) => {
    try {
      setUpdating('creating');
      
      // Clean and format data for backend
      const cleanedData = {
        name: bottleData.name,
        description: bottleData.description,
        price: parseFloat(bottleData.price),
        capacity: parseInt(bottleData.capacity),
        material: bottleData.material,
        closureType: bottleData.closureType,
        available: bottleData.available,
        premium: bottleData.premium,
        sustainablePackaging: bottleData.sustainablePackaging,
        recyclable: bottleData.recyclable
      };
      
      // Add optional fields only if they have values
      if (bottleData.color && bottleData.color !== '') {
        cleanedData.color = bottleData.color;
      }
      if (bottleData.shape && bottleData.shape !== '') {
        cleanedData.shape = bottleData.shape;
      }
      if (bottleData.image && bottleData.image !== '') {
        cleanedData.image = bottleData.image;
      }
      if (bottleData.manufacturer && bottleData.manufacturer !== '') {
        cleanedData.manufacturer = bottleData.manufacturer;
      }
      if (bottleData.origin && bottleData.origin !== '') {
        cleanedData.origin = bottleData.origin;
      }
      
      const response = await adminAPI.createBottle(cleanedData);
      if (response.success) {
        toast({
          title: "Bottiglia creata",
          description: `Bottiglia "${bottleData.name}" creata con successo`,
        });
        
        setShowCreateModal(false);
        resetForm();
        loadBottles();
      } else {
        throw new Error(response.message || 'Failed to create bottle');
      }
      
    } catch (error) {
      console.error('Error creating bottle:', error);
      toast({
        title: "Errore nella creazione",
        description: error.message || "Impossibile creare la bottiglia",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  const handleDeleteBottle = async (bottleId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa bottiglia? Questa azione non può essere annullata.')) {
      return;
    }

    try {
      setUpdating(bottleId);
      
      const response = await adminAPI.deleteBottle(bottleId);
      if (response.success) {
        toast({
          title: "Bottiglia eliminata",
          description: "La bottiglia è stata eliminata dal catalogo",
        });
        
        loadBottles();
      } else {
        throw new Error(response.message || 'Failed to delete bottle');
      }
      
    } catch (error) {
      console.error('Error deleting bottle:', error);
      toast({
        title: "Errore nell'eliminazione",
        description: error.message || "Impossibile eliminare la bottiglia",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  const toggleAvailability = async (bottleId, currentAvailability) => {
    try {
      setUpdating(bottleId);
      
      const response = await adminAPI.updateBottle(bottleId, { available: !currentAvailability });
      if (response.success) {
        toast({
          title: "Disponibilità aggiornata",
          description: `Bottiglia ${!currentAvailability ? 'attivata' : 'disattivata'}`,
        });
        
        loadBottles();
      } else {
        throw new Error(response.message || 'Failed to update availability');
      }
      
    } catch (error) {
      console.error('Error toggling availability:', error);
      toast({
        title: "Errore",
        description: error.message || "Impossibile aggiornare la disponibilità",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  const getMaterialColor = (material) => {
    const colors = {
      'vetro': 'bg-blue-100 text-blue-800',
      'cristallo': 'bg-purple-100 text-purple-800',
      'ceramica': 'bg-amber-100 text-amber-800',
      'plastica': 'bg-gray-100 text-gray-800'
    };
    return colors[material] || 'bg-gray-100 text-gray-800';
  };

  const openEditModal = (bottle) => {
    setSelectedBottle(bottle);
    setBottleForm({
      id: bottle.id || '',
      name: bottle.name || '',
      description: bottle.description || '',
      price: bottle.price?.toString() || '',
      capacity: bottle.capacity?.toString() || '500',
      material: bottle.material || 'vetro',
      color: bottle.color || '',
      shape: bottle.shape || '',
      closureType: bottle.closureType || 'sughero',
      available: bottle.available !== undefined ? bottle.available : true,
      premium: bottle.premium || false,
      image: bottle.image || '',
      manufacturer: bottle.manufacturer || '',
      origin: bottle.origin || '',
      sustainablePackaging: bottle.sustainablePackaging || false,
      recyclable: bottle.recyclable !== undefined ? bottle.recyclable : true
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setBottleForm({
      id: '',
      name: '',
      description: '',
      price: '',
      capacity: '500',
      material: 'vetro',
      color: '',
      shape: '',
      closureType: 'sughero',
      available: true,
      premium: false,
      image: '',
      manufacturer: '',
      origin: '',
      sustainablePackaging: false,
      recyclable: true
    });
  };

  const handleInputChange = (field, value) => {
    setBottleForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const filteredBottles = bottles.filter(bottle => {
    const matchesSearch = !filters.search || 
      bottle.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      bottle.description.toLowerCase().includes(filters.search.toLowerCase()) ||
      bottle.material.toLowerCase().includes(filters.search.toLowerCase());
    
    const matchesMaterial = !filters.material || bottle.material === filters.material;
    const matchesAvailability = filters.available === '' || bottle.available.toString() === filters.available;
    
    return matchesSearch && matchesMaterial && matchesAvailability;
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestione Bottiglie</h1>
            <p className="text-gray-600 mt-1">
              Gestisci i tipi di bottiglia disponibili per i liquori personalizzati
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
          >
            <Plus size={20} />
            Nuova Bottiglia
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cerca
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Nome, descrizione, materiale..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Materiale
              </label>
              <select
                value={filters.material}
                onChange={(e) => setFilters(prev => ({ ...prev, material: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Tutti i materiali</option>
                <option value="vetro">Vetro</option>
                <option value="cristallo">Cristallo</option>
                <option value="ceramica">Ceramica</option>
                <option value="plastica">Plastica</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Disponibilità
              </label>
              <select
                value={filters.available}
                onChange={(e) => setFilters(prev => ({ ...prev, available: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Tutte</option>
                <option value="true">Disponibili</option>
                <option value="false">Non disponibili</option>
              </select>
            </div>
            
            <div className="flex items-end">
              <button
                onClick={() => setFilters({ material: '', available: '', search: '' })}
                className="w-full px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Reset Filtri
              </button>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Totale Bottiglie</p>
                <p className="text-2xl font-bold text-gray-900">{bottles.length}</p>
              </div>
              <Wine className="text-purple-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Disponibili</p>
                <p className="text-2xl font-bold text-green-600">
                  {bottles.filter(bottle => bottle.available).length}
                </p>
              </div>
              <Eye className="text-green-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Premium</p>
                <p className="text-2xl font-bold text-purple-600">
                  {bottles.filter(bottle => bottle.premium).length}
                </p>
              </div>
              <Filter className="text-purple-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Materiali</p>
                <p className="text-2xl font-bold text-blue-600">
                  {new Set(bottles.map(bottle => bottle.material)).size}
                </p>
              </div>
              <Filter className="text-blue-500" size={24} />
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner />
          </div>
        )}

        {/* Bottles Table */}
        {!loading && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Bottiglia</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Materiale</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Prezzo</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Capacità</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Disponibilità</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredBottles.map((bottle) => (
                    <tr key={bottle.id} className="hover:bg-gray-50">
                      <td className="py-4 px-4">
                        <div>
                          <h3 className="font-medium text-gray-900">{bottle.name}</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {bottle.description?.substring(0, 60)}...
                          </p>
                          {bottle.premium && (
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 mt-1">
                              Premium
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getMaterialColor(bottle.material)}`}>
                          {bottle.material.charAt(0).toUpperCase() + bottle.material.slice(1)}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-1">
                          <Euro size={14} className="text-gray-400" />
                          <span className="font-medium text-gray-900">
                            {bottle.price?.toFixed(2)}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm text-gray-600">
                          {bottle.capacity} ml
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <button
                          onClick={() => toggleAvailability(bottle.id, bottle.available)}
                          disabled={updating === bottle.id}
                          className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            bottle.available
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-red-100 text-red-800 hover:bg-red-200'
                          } ${updating === bottle.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {updating === bottle.id ? (
                            <LoadingSpinner size="xs" />
                          ) : bottle.available ? (
                            <Eye size={12} />
                          ) : (
                            <EyeOff size={12} />
                          )}
                          {bottle.available ? 'Disponibile' : 'Non disponibile'}
                        </button>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEditModal(bottle)}
                            disabled={updating === bottle.id}
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Modifica bottiglia"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteBottle(bottle.id)}
                            disabled={updating === bottle.id}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Elimina bottiglia"
                          >
                            {updating === bottle.id ? (
                              <LoadingSpinner size="xs" />
                            ) : (
                              <Trash2 size={16} />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {filteredBottles.length === 0 && (
              <div className="text-center py-12">
                <Wine className="mx-auto text-gray-400 mb-4" size={48} />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Nessuna bottiglia trovata</h3>
                <p className="text-gray-600 mb-4">
                  {filters.search || filters.material || filters.available 
                    ? 'Prova a modificare i filtri di ricerca.' 
                    : 'Inizia aggiungendo la tua prima bottiglia.'}
                </p>
                {!filters.search && !filters.material && !filters.available && (
                  <button
                    onClick={() => {
                      resetForm();
                      setShowCreateModal(true);
                    }}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Crea Prima Bottiglia
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Create/Edit Modal */}
        {(showCreateModal || showEditModal) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">
                  {showCreateModal ? 'Crea Nuova Bottiglia' : 'Modifica Bottiglia'}
                </h2>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setSelectedBottle(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome Bottiglia *
                    </label>
                    <input
                      type="text"
                      value={bottleForm.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="es. Bottiglia Premium"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Materiale *
                    </label>
                    <select
                      value={bottleForm.material}
                      onChange={(e) => handleInputChange('material', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required
                    >
                      <option value="vetro">Vetro</option>
                      <option value="cristallo">Cristallo</option>
                      <option value="ceramica">Ceramica</option>
                      <option value="plastica">Plastica</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrizione *
                  </label>
                  <textarea
                    value={bottleForm.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Descrivi la bottiglia, le sue caratteristiche..."
                    required
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prezzo (€) *
                    </label>
                    <div className="relative">
                      <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={bottleForm.price}
                        onChange={(e) => handleInputChange('price', e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="0.00"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Capacità (ml)
                    </label>
                    <input
                      type="number"
                      min="100"
                      max="2000"
                      value={bottleForm.capacity}
                      onChange={(e) => handleInputChange('capacity', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Chiusura
                    </label>
                    <input
                      type="text"
                      value={bottleForm.closureType}
                      onChange={(e) => handleInputChange('closureType', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="sughero"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Colore
                    </label>
                    <input
                      type="text"
                      value={bottleForm.color}
                      onChange={(e) => handleInputChange('color', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="es. trasparente, ambrato"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Forma
                    </label>
                    <input
                      type="text"
                      value={bottleForm.shape}
                      onChange={(e) => handleInputChange('shape', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="es. classica, elegante"
                    />
                  </div>
                </div>
                
                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Immagine Bottiglia
                  </label>
                  <FileUpload
                    category="bottles"
                    itemId={bottleForm.id || 'new'}
                    currentImage={bottleForm.image}
                    onImageUploaded={(imagePath) => 
                      handleInputChange('image', imagePath)
                    }
                  />
                </div>
                
                {/* Options */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="available"
                      checked={bottleForm.available}
                      onChange={(e) => handleInputChange('available', e.target.checked)}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <label htmlFor="available" className="ml-2 text-sm text-gray-700">
                      Disponibile
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="premium"
                      checked={bottleForm.premium}
                      onChange={(e) => handleInputChange('premium', e.target.checked)}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <label htmlFor="premium" className="ml-2 text-sm text-gray-700">
                      Premium
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="sustainablePackaging"
                      checked={bottleForm.sustainablePackaging}
                      onChange={(e) => handleInputChange('sustainablePackaging', e.target.checked)}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <label htmlFor="sustainablePackaging" className="ml-2 text-sm text-gray-700">
                      Sostenibile
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="recyclable"
                      checked={bottleForm.recyclable}
                      onChange={(e) => handleInputChange('recyclable', e.target.checked)}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <label htmlFor="recyclable" className="ml-2 text-sm text-gray-700">
                      Riciclabile
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setSelectedBottle(null);
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={updating}
                >
                  Annulla
                </button>
                <button
                  onClick={() => {
                    if (showCreateModal) {
                      handleCreateBottle(bottleForm);
                    } else {
                      handleUpdateBottle(selectedBottle.id, bottleForm);
                    }
                  }}
                  disabled={updating || !bottleForm.name || !bottleForm.description || !bottleForm.price}
                  className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {updating ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <Save size={16} />
                  )}
                  {showCreateModal ? 'Crea Bottiglia' : 'Salva Modifiche'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminBottles;